/**
 * Lender Session Tracking Utility
 *
 * Tracks which member scores a lender has viewed during their session.
 * Used for audit traceability - stores run_id from algo_runs table.
 *
 * Epic 16 Sub-Phase 1E: UI Instrumentation for Audit Framework
 * - Local session tracking (sessionStorage) for UI state
 * - API calls to log access events to lender_access_events table
 *
 * Storage Format:
 * {
 *   session_start: "2025-12-31T10:49:00Z",
 *   members_displayed: {
 *     "member-uuid-1": {
 *       run_id: "run-123",
 *       member_name: "Patricia Brown",
 *       score: 67,
 *       displayed_at: "2025-12-31T10:49:00Z",
 *       expanded_at: null
 *     },
 *     ...
 *   }
 * }
 */

import { logAccessEvent } from '../services/lenderApiService';

const STORAGE_KEY = 'verascore_lender_session';

export interface MemberViewRecord {
  run_id: string | undefined;
  member_name: string;
  score: number;
  displayed_at: string;
  expanded_at: string | null;
}

export interface LenderSessionData {
  session_start: string;
  members_displayed: Record<string, MemberViewRecord>;
}

/**
 * Get current session data from sessionStorage
 */
export function getLenderSessionData(): LenderSessionData | null {
  try {
    const data = sessionStorage.getItem(STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[lenderSessionTracking] Failed to parse session data:', error);
  }
  return null;
}

/**
 * Initialize or get existing session
 */
function getOrCreateSession(): LenderSessionData {
  const existing = getLenderSessionData();
  if (existing) {
    return existing;
  }
  return {
    session_start: new Date().toISOString(),
    members_displayed: {},
  };
}

/**
 * Save session data to sessionStorage
 */
function saveSessionData(data: LenderSessionData): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[lenderSessionTracking] Failed to save session data:', error);
  }
}

/**
 * Record that members were displayed in the composite view
 * Called when getMembers() API returns data
 *
 * Also logs VIEW_SCORE access events to backend for FCRA compliance
 * Only logs once per session per member (to avoid duplicate logging on refresh)
 */
export function recordMembersDisplayed(
  members: Array<{
    uuid: string;
    member_name: string;
    // Phase 9G: Score fields are nullable (no official score ≠ zero score)
    currentScore?: number | null;
    verascore?: number | null;
    run_id?: string;
  }>
): void {
  const session = getOrCreateSession();
  const now = new Date().toISOString();
  const newlyDisplayedMembers: typeof members = [];

  for (const member of members) {
    const score = member.currentScore ?? member.verascore ?? 0;

    // Only add if not already tracked (preserve expanded_at if already set)
    if (!session.members_displayed[member.uuid]) {
      session.members_displayed[member.uuid] = {
        run_id: member.run_id,
        member_name: member.member_name,
        score,
        displayed_at: now,
        expanded_at: null,
      };
      // Track this as newly displayed for API logging
      newlyDisplayedMembers.push(member);
    } else {
      // Update run_id and score if they've changed (e.g., data refresh)
      session.members_displayed[member.uuid].run_id = member.run_id;
      session.members_displayed[member.uuid].score = score;
    }
  }

  saveSessionData(session);
  if (import.meta.env.DEV) console.log(`[lenderSessionTracking] Recorded ${members.length} members displayed (${newlyDisplayedMembers.length} new)`);

  // Log VIEW_SCORE access events for newly displayed members (fire-and-forget)
  // Only log for members we haven't logged before in this session
  for (const member of newlyDisplayedMembers) {
    logAccessEvent({
      member_id: member.uuid,
      access_type: 'VIEW_SCORE',
      run_id: member.run_id,
      fields_accessed: ['verascore', 'verascore_rating'],
    }).catch((err) => {
      // Silently fail - don't break UI for audit logging
      if (import.meta.env.DEV) console.warn('[lenderSessionTracking] Failed to log VIEW_SCORE event:', err);
    });
  }
}

/**
 * Record that a member card was expanded for detailed view
 * Called when user expands a MemberCard
 *
 * Also logs to backend lender_access_events table for audit trail
 */
export function recordMemberExpanded(memberId: string): void {
  const session = getOrCreateSession();
  const now = new Date().toISOString();

  if (session.members_displayed[memberId]) {
    const memberRecord = session.members_displayed[memberId];
    memberRecord.expanded_at = now;
    saveSessionData(session);
    if (import.meta.env.DEV) console.log(`[lenderSessionTracking] Recorded member ${memberId} expanded`);

    // Log to backend for audit trail (fire-and-forget)
    logAccessEvent({
      member_id: memberId,
      access_type: 'VIEW_FULL_PROFILE',
      run_id: memberRecord.run_id,
      fields_accessed: [
        'verascore',
        'verascore_rating',
        'factor_dti',
        'factor_liquid_reserves',
        'factor_savings',
        'factor_cash_flow',
        'factor_debt',
        'factor_payment_history',
        'yearly_gross_income',
        'monthly_gross_income',
      ],
    }).catch((err) => {
      // Silently fail - don't break UI for audit logging
      if (import.meta.env.DEV) console.warn('[lenderSessionTracking] Failed to log access event:', err);
    });
  } else {
    if (import.meta.env.DEV) console.warn(`[lenderSessionTracking] Member ${memberId} not in session - was it displayed?`);
  }
}

/**
 * Get run_id for a specific member
 */
export function getMemberRunId(memberId: string): string | undefined {
  const session = getLenderSessionData();
  return session?.members_displayed[memberId]?.run_id;
}

/**
 * Clear session data (e.g., on logout)
 */
export function clearLenderSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    if (import.meta.env.DEV) console.log('[lenderSessionTracking] Session cleared');
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[lenderSessionTracking] Failed to clear session:', error);
  }
}

